import { dateLocale } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const pad = (n) => String(n).padStart(2, "0");
export const LEGEND = [
  ["bg-red-500", "Festività / chiusura"],
  ["bg-amber-400", "Periodo bloccato"],
  ["bg-orange-400", "Già richiesto"],
  ["bg-sky-500", "Colleghi assenti"],
  ["bg-primary", "Selezione"],
];

export function useSpecialDays(y, m, rev = 0) {
  const [sp, setSp] = useState({ holidays: [], closures: [], taken: [], others: {} });
  useEffect(() => { api.get("/calendar/special", { params: { year: y, month: m } }).then((r) => setSp(r.data)); }, [y, m, rev]);
  const inRange = (list, iso) => list.find((x) => x.start_date <= iso && iso <= x.end_date);
  return {
    ...sp,
    holiday: (iso) => sp.holidays.find((h) => h.date === iso),
    closure: (iso) => inRange(sp.closures.filter((c) => c.kind === "closure"), iso),
    blackout: (iso) => inRange(sp.closures.filter((c) => c.kind === "blackout"), iso),
    taken: (iso) => inRange(sp.taken, iso),
    overlap: (iso) => sp.others?.[iso] || 0,
  };
}

export function LeaveMiniCalendar({ start, end, onSelect }) {
  const [picking, setPicking] = useState(false);
  const pick = (iso) => {
    if (!picking || iso < start) {
      onSelect(iso, iso);
      setPicking(true);
    } else {
      onSelect(start, iso);
      setPicking(false);
    }
  };
  const base = start ? new Date(start + "T00:00:00") : new Date();
  const [ym, setYm] = useState({ y: base.getFullYear(), m: base.getMonth() + 1 });
  useEffect(() => { if (start) setYm({ y: +start.slice(0, 4), m: +start.slice(5, 7) }); }, [start]);
  const sp = useSpecialDays(ym.y, ym.m);
  const daysIn = new Date(ym.y, ym.m, 0).getDate();
  const lead = (new Date(ym.y, ym.m - 1, 1).getDay() + 6) % 7;
  const shift = (d) => setYm(({ y, m }) => { const t = m - 1 + d; return { y: y + Math.floor(t / 12), m: ((t % 12) + 12) % 12 + 1 }; });

  return (
    <div className="rounded-md border p-3" data-testid="leave-mini-calendar">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" onClick={() => shift(-1)} className="rounded p-1 hover:bg-muted" data-testid="mini-cal-prev"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-sm font-medium capitalize">{new Date(ym.y, ym.m - 1, 1).toLocaleDateString(dateLocale(), { month: "long", year: "numeric" })}</span>
        <button type="button" onClick={() => shift(1)} className="rounded p-1 hover:bg-muted" data-testid="mini-cal-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[11px]">
        {["L1", "M2", "M3", "G4", "V5", "S6", "D7"].map((d) => <span key={d} className="py-0.5 text-muted-foreground">{d[0]}</span>)}
        {Array.from({ length: lead }).map((_, i) => <span key={`l${i}`} />)}
        {Array.from({ length: daysIn }).map((_, i) => {
          const iso = `${ym.y}-${pad(ym.m)}-${pad(i + 1)}`;
          const sel = start && end && start <= iso && iso <= end;
          const h = sp.holiday(iso) || sp.closure(iso);
          const b = sp.blackout(iso);
          const t = sp.taken(iso);
          const o = sp.overlap(iso);
          const title = [h && (h.name || h.title), b && `Bloccato: ${b.title}`, t && "Già richiesto da te", o > 0 && `${o} ${o === 1 ? "collega assente" : "colleghi assenti"}`].filter(Boolean).join(" · ");
          return (
            <button type="button" key={iso} title={title} onClick={() => pick(iso)} data-testid={`mini-cal-day-${iso}`}
              className={cn("relative rounded py-1.5 transition-colors hover:ring-2 hover:ring-inset hover:ring-primary/50",
                sel ? "bg-primary font-semibold text-primary-foreground" : h ? "bg-red-500/15 font-medium text-red-600 dark:text-red-400"
                  : b ? "bg-amber-400/25 text-amber-800 dark:text-amber-300" : t ? "bg-orange-400/25 text-orange-700 dark:text-orange-300"
                  : o > 0 ? "bg-sky-500/20 font-medium text-sky-800 dark:text-sky-300" : (lead + i) % 7 >= 5 ? "text-muted-foreground" : "",
                sel && (h || b || t) && "ring-2 ring-inset ring-red-400")}>
              {i + 1}
              {o > 0 && <span className={cn("absolute right-0.5 top-0.5 min-w-[13px] rounded-full px-0.5 text-[9px] font-bold leading-[13px]", sel ? "bg-white text-sky-700" : "bg-sky-500 text-white")} data-testid={`mini-cal-overlap-${iso}`}>{o}</span>}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground" data-testid="mini-cal-hint">{picking ? "Ora seleziona l'ultimo giorno" : "Clicca il primo e poi l'ultimo giorno di ferie"}</p>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {LEGEND.map(([c, l]) => <span key={l} className="inline-flex items-center gap-1"><span className={cn("h-2 w-2 rounded-full", c)} />{l}</span>)}
      </div>
    </div>
  );
}
